import { PEER_LINES } from './content/stats';

export const TARGET_SCORE = 80;
export const MAX_DISPLAY_SCORE = 79.9;

const PER_ITEM_POINTS = { 'ai-personal': 1, 'ai-tools': 2, 'ai-explain': 4 };
const ITEM_POINT_CAPS = { 'ai-personal': 5, 'ai-tools': 10, 'ai-explain': 20 };

const SINGLE_ANSWER_POINTS = {
  'ai-at-work': { 'Every day': 15, 'Every week': 9, 'Not often': 3, Never: 0 },
  'ai-models': { 'Yes, I pick one for each task': 10, 'Sometimes I switch': 6 },
  'ai-connectors': { 'Yes, all of them': 10, 'Yes, some of them': 6 },
  'ai-skills': { 'Yes, I use them a lot': 10, 'Yes, a few simple rules': 5 },
  'ai-agents': { 'Yes, a lot': 20, 'Yes, a little': 14, 'I tried but stopped': 5 },
};

export const NON_ANSWERS = ['None of these'];

const TIERS = [
  {
    id: 'falling-behind',
    label: 'Falling behind',
    min: 0,
    headline: 'You are falling behind',
    message: 'People who work with AI every day are pulling away from where you are now.',
  },
  {
    id: 'exposed',
    label: 'Exposed',
    min: 40,
    headline: 'You are exposed',
    message: "You use AI, but you haven't built the skills that keep you ahead of it.",
  },
  {
    id: 'ahead',
    label: 'Ahead, for now',
    min: 70,
    headline: 'You are ahead, for now',
    message: 'You know more than most, but the tools are changing every few months.',
  },
];

const countItems = (answer) =>
  Array.isArray(answer) ? answer.filter((item) => !NON_ANSWERS.includes(item)).length : 0;

const explains = (answers, concept) => (answers['ai-explain'] || []).includes(concept);

const singlePoints = (answers, id) => SINGLE_ANSWER_POINTS[id][answers[id]] || 0;

const getGaps = (answers) => {
  const gaps = [
    {
      id: 'models',
      title: 'Choosing the right model',
      description: 'Different models suit different work. Most people only use the default.',
      isMissing: singlePoints(answers, 'ai-models') < 6,
    },
    {
      id: 'harnesses',
      title: 'Harnesses',
      description: 'The tools around a model that let it read files, run commands and finish real tasks.',
      isMissing: !explains(answers, 'What a harness is'),
    },
    {
      id: 'agents',
      title: 'Agents',
      description: 'AI that works through multi-step tasks, on a schedule or a trigger, without you asking.',
      isMissing: !explains(answers, 'AI agents') || singlePoints(answers, 'ai-agents') < 14,
    },
    {
      id: 'skills',
      title: 'AI skills',
      description: 'Reusable instructions that teach AI how you want a job done, every time.',
      isMissing: !explains(answers, 'AI skills') || singlePoints(answers, 'ai-skills') < 5,
    },
    {
      id: 'plugins',
      title: 'Plugins',
      description: 'Links that give AI access to your email, calendar, files and work tools.',
      isMissing: !explains(answers, 'Plugins') || singlePoints(answers, 'ai-connectors') < 6,
    },
    {
      id: 'workflows',
      title: 'AI workflows',
      description: 'Chains of AI steps that turn a repeated task into something that runs by itself.',
      isMissing: !explains(answers, 'AI workflows'),
    },
  ];

  return gaps.filter((gap) => gap.isMissing).map(({ isMissing, ...gap }) => gap);
};

export const computeFluencyScore = (answers = {}) => {
  const itemPoints = Object.keys(PER_ITEM_POINTS).reduce(
    (total, id) =>
      total + Math.min(countItems(answers[id]) * PER_ITEM_POINTS[id], ITEM_POINT_CAPS[id]),
    0
  );
  const answerPoints = Object.keys(SINGLE_ANSWER_POINTS).reduce(
    (total, id) => total + singlePoints(answers, id),
    0
  );

  const rawScore = itemPoints + answerPoints;
  const score = Math.min(rawScore, MAX_DISPLAY_SCORE);
  const tier = [...TIERS].reverse().find(({ min }) => score >= min);

  return {
    rawScore,
    score,
    tier,
    gaps: getGaps(answers),
    peerLine: PEER_LINES[answers['ai-at-work']] || PEER_LINES.Never,
  };
};
